import { Image, Swiper, Toast } from 'antd-mobile-v5'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useIntl } from 'react-intl'

import BuyOptions from './BuyOptions'
import AllCardsList from './AllCardsList'
import {
  useAddToCart,
  useCardOptions,
  useClubVipFuiouPay,
  useClubVipPaymentStatus,
  useEcardGoods,
  useHasActivity,
  useShopCart,
} from '../../services/hooks'
import { useClubVipPayment } from '../../services/useClubVipPayment'
import LoadingStatus from '../../components/FormTools/Loading'
import PublicWelfareIntro from './components/PublicWelfareIntro'
import StoryPopup from './components/StoryPopup'
import { getCertItemNo, getMinPriceOptions, getSkuInfo } from './cardOptionUtils'
import { CLUB_URL_DOMAIN } from '../../utils/env'
import { getCertIdsInCart, UClick } from '../../components/common/utils'
import { cardStories } from '../../utils/cardStories'
import { getChangeRight, getEcards, getUserInfo } from '../../reducers/selectors'
import ValueAddedServices from '../../components/ValueAddedServices/ValueAddedServices'
import ChanghuanPopup from './components/ChanghuanPopup'
import QrCodePay from '../../components/QrCodePay/QrCodePay'
import EcardCheckoutPopup from '../../components/Cart/EcardCheckoutPopup'

import styles from './BuyCards.module.css'

const HORSE_YEAR_ACTIVITY_CODE = 17
const HORSE_YEAR_KEYWORD = '马年'

export default () => {
  const { push, replace } = useHistory()
  const { formatMessage } = useIntl()
  const [cardOptions, setCardOptions] = useState([])
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [currentStory, setCurrentStory] = useState(null)
  const [showAllCards, setShowAllCards] = useState(false)
  const [storyVisible, setStoryVisible] = useState(false)
  const [selectedCerts, setSelectedCerts] = useState([])
  const [cart, setCart] = useState([])
  const [selectedImage, setSelectedImage] = useState('')
  const [isPlastic, setIsPlastic] = useState(true)
  const [defaultIndex, setDefaultIndex] = useState(0)
  const [valueAdded, setValueAdded] = useState([])
  // 商品目录（ecard_goods）：createEcardOrder 的 itemNo 唯一取值来源（cardOptions 有赞体系无 itemNo）
  const { data: goodsCatalog } = useEcardGoods()
  const { state, search } = useLocation()
  const [changhuanVisible, setChanghuanVisible] = useState(false)
  const [showCheckout, setShowCheckout] = useState(false)
  const [checkoutItems, setCheckoutItems] = useState([])

  const { userInfo, ecards, hasChangeRight } = useSelector((state) => ({
    ecards: getEcards(state),
    userInfo: getUserInfo(state),
    hasChangeRight: getChangeRight(state),
  }))

  const { loading, data } = useCardOptions({
    ready: !!userInfo,
    onSuccess: (data) => {
      let minPriceOptions = getMinPriceOptions(data)
      minPriceOptions.forEach((option, index) => {
        option.id = index
      })
      setSelectedImage(minPriceOptions[0].styleName)
      setCardOptions(minPriceOptions)
      getShopCart()
    },
  })

  const { run: addToCartSubmit, loading: addLoading } = useAddToCart({
    onSuccess: () => Toast.show({ icon: 'success', content: '购物车已更新' }),
  })

  const { data: activityData } = useHasActivity(HORSE_YEAR_ACTIVITY_CODE, {
    ready: !!userInfo,
  })

  const { loading: shopCartLoading, run: getShopCart } = useShopCart({
    onSuccess: (data) => {
      setCart(data)
    },
  })

  const { runAsync: fuiouPay } = useClubVipFuiouPay({})
  // 轮询失败时抑制 useRequestCommon 的全局错误 Modal（轮询类请求不弹窗，与 BooksCheckout/AgentElearning 一致）
  const { runAsync: checkFuiouStatus } = useClubVipPaymentStatus({ onError: () => true })

  const { toPay, nativeQrCode, nativePrice, nativeCurrency, nativePayDisplay, setNativePayDisplay } = useClubVipPayment(
    {
      type: 'ECARDS_CHANGE',
      fuiouPay,
      checkFuiouStatus,
    }
  )

  const previewRedirectedRef = useRef(false)
  // 进入页面埋点（仅随登录态触发，避免 search 变化二次触发）
  useEffect(() => {
    if (userInfo) UClick('buyCardsPageA')
  }, [userInfo])
  // preview 来源重定向：只执行一次，避免 replace 改写 search 后重复埋点
  useEffect(() => {
    if (!userInfo || !search.includes('src=preview') || previewRedirectedRef.current) return
    previewRedirectedRef.current = true
    UClick('buyCardsPreviewToBuyCards')
    replace('/buy_cards')
  }, [userInfo, replace, search])

  useEffect(() => {
    const hasCardOptions = cardOptions.length > 0
    if (hasCardOptions && userInfo) {
      UClick(`buyCards-index`)
      UClick(`CardOption${cardOptions[0].styleId}`)
    }
  }, [cardOptions, userInfo])

  useEffect(() => {
    setCurrentStory(null)
    const story = cardStories.find(
      (s) => cardOptions[selectedIndex]?.cardStockId && s.id === cardOptions[selectedIndex].cardStockId
    )
    if (story) {
      setCurrentStory(story)
    }
  }, [selectedIndex, cardOptions])

  useEffect(() => {
    if (state && cardOptions.length > 0) {
      const { cardImageName, ecardId } = state
      // 从证书详情页跳转时，保留当前证书选中状态
      if (ecardId) {
        setSelectedCerts([ecardId])
      }
      // 匹配当前卡面：cardImageName 来自详情页 currentCardDetail.desc，
      // 与 cardOptions 的 styleName 对应（来自同一后端卡面名称体系）
      if (cardImageName) {
        const matchedCard = cardOptions.find((o) => o.styleName === cardImageName)
        if (matchedCard) {
          setSelectedImage(cardImageName)
          const defaultIndex = cardOptions.findIndex((o) => o.styleName === cardImageName)
          setSelectedIndex(defaultIndex)
          setDefaultIndex(defaultIndex)
        }
      }
    }
  }, [state, cardOptions])

  const toggleCard = useCallback(
    (index) => {
      UClick(`CardOption${cardOptions[index].styleId || 'ChangHuan'}`)
      setSelectedIndex(index)
      setSelectedImage(cardOptions[index].styleName)
      setIsPlastic(true)
      setSelectedCerts([])
      if (index === 3) {
        UClick('buyCardsPage99')
      }
    },
    [cardOptions]
  )

  const buildCertSkuList = useCallback(() => {
    const certSkuList = []
    selectedCerts.forEach((certId) => {
      const ecard = ecards.find((cert) => cert.encodedId === certId)
      if (!ecard) return

      const skuInfo = getSkuInfo(ecard, data, selectedImage, isPlastic)
      // SKU 未匹配（陈旧缓存缺 cardRequestType、后端下架等异常数据）时跳过该证书：
      // 防御解构 undefined 抛 TypeError（React 16 不捕获事件处理器异常），
      // 下单入口的 itemNo 校验会兜底拦截空明细
      if (!skuInfo) return
      const currentStory = cardStories.find((s) => selectedImage === s.name)
      const description = currentStory?.author?.name || null
      const { cardRequestType, ecardType, imageUrl, goodsId, skuId, id, ...sku } = skuInfo
      const certSku = {
        ecardId: ecard.encodedId,
        description,
        ecardName: ecard.ecardName,
        goodsId,
        skuId,
        ...sku,
        // createEcardOrder 必传 itemNo：按卡面 cardStockId + 卡类型在 ecard_goods 目录匹配；
        // 目录未配置时返回 null，由结算弹窗提交前校验拦截并提示
        itemNo: getCertItemNo(goodsCatalog, { cardStockId: sku.cardStockId, isPlastic }),
      }
      certSkuList.push(certSku)
    })
    return certSkuList
  }, [selectedCerts, ecards, data, selectedImage, isPlastic, goodsCatalog])

  const trackValueAdded = useCallback(
    (prefix) => {
      if (valueAdded.length === 2) {
        UClick(`${prefix}-all`)
      } else if (valueAdded.length === 0) {
        UClick(`${prefix}-null`)
      } else {
        valueAdded.forEach((v) => {
          UClick(`${prefix}-${v.type}`)
        })
      }
    },
    [valueAdded]
  )

  const hasStoryCard = useCallback((styleName) => {
    return cardStories.find((story) => story.name.indexOf(styleName) >= 0)
  }, [])

  const updateCart = useCallback(() => {
    let newCart = [...cart]
    const certSkuList = buildCertSkuList()
    // 排重键 (ecardId, itemNo)：rich 条目（本地构建）itemNo 缺失时按 skuId 合成
    // "SKU-{skuId}"，与服务端购物车 slim 条目的 itemNo 同构比对
    const cartItemKey = (item) =>
      `${item.ecardId}|${item.itemNo ?? (item.skuId != null ? `SKU-${item.skuId}` : undefined)}`
    newCart = newCart.filter((cartItem) => {
      return !certSkuList.some((sku) => cartItemKey(sku) === cartItemKey(cartItem))
    })
    return [...certSkuList, ...newCart]
  }, [cart, buildCertSkuList])

  const addToCart = useCallback(() => {
    if (selectedCerts.length === 0) {
      Toast.show({ content: '请选择证书。' })
      return
    }
    const certsIdsInCart = getCertIdsInCart(ecards, cart, selectedImage, isPlastic)
    trackValueAdded('addAddedToCartA')
    if (certsIdsInCart.length > 0) {
      Toast.show({
        content: '您购物袋中已经有相似卡面，请到购物袋中确认订单。',
      })
    }
    UClick('addEcardsToCart')
    const newCart = updateCart()
    setCart(newCart)
    addToCartSubmit(newCart)
  }, [selectedCerts, ecards, cart, selectedImage, isPlastic, trackValueAdded, updateCart, addToCartSubmit])

  const backToHome = useCallback(() => {
    window.location.href = CLUB_URL_DOMAIN
  }, [])

  const toBuyCards = useCallback(() => {
    // CHNA-2618：增值服务（实体卡85折/周边8折）不能单独购买，必须搭配至少一个已选卡面的卡
    if (selectedCerts.length === 0 && valueAdded.length === 0) {
      Toast.show({ content: formatMessage({ id: 'buycards-select-cert-or-added' }) })
      return
    }
    if (valueAdded.length > 0 && selectedCerts.length === 0) {
      Toast.show({ content: formatMessage({ id: 'ecard-rights-requires-card' }) })
      return
    }
    const certSkuList = buildCertSkuList()
    UClick('ClickBuyCardBtn')
    trackValueAdded('clickBuyAddedA')
    setCheckoutItems(certSkuList)
    setShowCheckout(true)
  }, [selectedCerts, buildCertSkuList, trackValueAdded, valueAdded, formatMessage])

  const toCartPage = useCallback(() => {
    if (addLoading) return
    push('/cart', { added: valueAdded })
  }, [addLoading, push, valueAdded])

  const isHorseYearCard = useMemo(
    () => cardOptions[selectedIndex]?.styleName?.includes(HORSE_YEAR_KEYWORD),
    [cardOptions, selectedIndex]
  )

  const is2026 = useMemo(() => new Date().getFullYear() === 2026, [])

  const showHorseYearTips = useMemo(
    () => isHorseYearCard && is2026 && activityData?.hasCard === false,
    [isHorseYearCard, is2026, activityData]
  )

  const horseYearTipsText = useMemo(() => formatMessage({ id: 'buycards-horseyear-tips' }), [formatMessage])

  if (loading || !data) return <LoadingStatus />

  return (
    <>
      <QrCodePay
        visible={nativePayDisplay}
        setVisible={setNativePayDisplay}
        url={nativeQrCode}
        price={nativePrice}
        currency={nativeCurrency}
      />
      <ChanghuanPopup
        visible={changhuanVisible}
        setVisible={setChanghuanVisible}
        toPay={toPay}
        hasChanghuanRights={hasChangeRight}
      />
      <EcardCheckoutPopup
        visible={showCheckout}
        setVisible={setShowCheckout}
        items={checkoutItems}
        valueAdded={valueAdded}
      />
      <div className={styles.container}>
        <img
          src={require('../../assets/images/buy_cards/back-home.png')}
          alt="back"
          className={styles['back-home-icon']}
          onClick={backToHome}
        />
        <div className={styles.flexible}>
          <Image src={require('../../assets/images/buy_cards/banner3.png')} lazy />
          <div className={styles['card-options']}>
            <div className={styles['options-header']}>
              <div className={styles.title}>卡面选择</div>
              <div className={styles.extra} onClick={() => setShowAllCards(true)}>
                查看所有卡片
              </div>
            </div>
            <div className={styles.options}>
              <Swiper
                defaultIndex={defaultIndex}
                key={defaultIndex}
                trackOffset={10}
                slideSize={80}
                indicator={() => null}
                onIndexChange={toggleCard}
              >
                {cardOptions.map((option, index) => {
                  const story = hasStoryCard(option.styleName)
                  return (
                    <Swiper.Item className={styles['option-item']} key={index}>
                      <div className={styles['card-wrapper']}>
                        <div style={{ paddingBottom: '65.79%', position: 'relative' }}>
                          <img src={option.imageUrl} className={styles.card} alt="card" />
                        </div>
                        <div className={styles.content}>
                          <div className={styles['card-name']}>{option.styleName}</div>
                          {story && (
                            <div className={styles['story-box']} onClick={() => setStoryVisible(true)}>
                              <img
                                src={require(
                                  `../../assets/images/buy_cards/${story.author.imgName || 'default-author'}.png`
                                )}
                                alt="avator"
                                className={styles.avator}
                              />
                              <div className={styles['story-detail']}>了解卡面故事</div>
                            </div>
                          )}
                        </div>
                      </div>
                    </Swiper.Item>
                  )
                })}
              </Swiper>
            </div>
          </div>
          <div>
            <div className={styles['options-box']}>
              <BuyOptions
                cardOptions={data}
                setCart={setCart}
                selectedImage={selectedImage}
                isPlastic={isPlastic}
                setIsPlastic={setIsPlastic}
                shopCartLoading={shopCartLoading}
                setSelectedCerts={setSelectedCerts}
                selectedCerts={selectedCerts}
                hasChanghuanRights={hasChangeRight}
              />
            </div>
            <div style={{ padding: '16px 12px 0' }}>
              <ValueAddedServices valueAdded={valueAdded} setValueAdded={setValueAdded} page={'buy'} />
            </div>
            <AllCardsList visible={showAllCards} setVisible={setShowAllCards} cardOptions={cardOptions} />
            {currentStory && <StoryPopup visible={storyVisible} setVisible={setStoryVisible} story={currentStory} />}
            <PublicWelfareIntro />
          </div>
        </div>
        <div className={styles.bottom}>
          <div className={styles.left} onClick={toCartPage}>
            <img src={require('../../assets/images/cart/cart-icon.png')} className={styles.icon} alt="cart" />
            <div className={styles.count}>{cart.length}</div>
          </div>
          <div className={styles.right}>
            {showHorseYearTips ? (
              <button className={styles['tips-btn']}>{horseYearTipsText}</button>
            ) : (
              <>
                <button className={styles['add-cart-btn']} onClick={addToCart}>
                  加入购物袋
                </button>
                <button className={styles['buy-btn']} onClick={toBuyCards}>
                  立即购买
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
